import { useEffect, useState } from "react";
import { Plus, Trash2, Scissors, Save, Printer, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const COLORS = ["#E53935", "#FFFFFF", "#B71C1C", "#FFCDD2", "#D32F2F", "#F5F5F5", "#8B0000", "#FF8A80", "#C62828", "#FFEBEE"];

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
};

const groupBarCuts = (cuts) => {
  const groups = [];
  const index = {};
  cuts.forEach((c) => {
    const key = `${c.label}__${c.length}`;
    if (!(key in index)) { index[key] = groups.length; groups.push({ label: c.label, length: c.length, qty: 0 }); }
    groups[index[key]].qty += 1;
  });
  return groups;
};

export default function CutOptimizer() {
  const [stockLength, setStockLength] = useState(6000);
  const [kerf, setKerf] = useState(3);
  const [pieces, setPieces] = useState([{ material: "", label: "", length: "", quantity: 1 }]);
  const [result, setResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planName, setPlanName] = useState("");
  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [workOrderId, setWorkOrderId] = useState("");
  const [company, setCompany] = useState(null);

  const loadPlans = () => api.get("/cuts/plans").then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => {
    loadPlans();
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/users").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/company/profile").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const orderLabel = (id) => {
    const o = orders.find((x) => x.id === id);
    return o ? o.number : null;
  };

  const orderJobName = (id) => {
    const o = orders.find((x) => x.id === id);
    if (!o) return null;
    return [o.client_name, o.title].filter(Boolean).join(" — ");
  };

  const linkPlan = async (planId, orderId) => {
    try {
      await api.patch(`/cuts/plans/${planId}`, { work_order_id: orderId || null });
      loadPlans();
      toast.success(orderId ? "Plan vinculado a la orden" : "Plan desvinculado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const assignPlan = async (planId, userId) => {
    try {
      await api.patch(`/cuts/plans/${planId}`, { assigned_to: userId || null });
      loadPlans();
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      toast.success(userId ? "Plan de corte asignado — se vinculó/creó la orden con la tarea de corte" : "Asignación quitada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Eliminar el plan de corte "${plan.name}" definitivamente?`)) return;
    try {
      await api.delete(`/cuts/plans/${plan.id}`);
      loadPlans();
      toast.success("Plan de corte eliminado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPiece = (i, k, v) => setPieces(pieces.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));

  const optimize = async (save = false) => {
    const valid = pieces.filter((p) => Number(p.length) > 0 && Number(p.quantity) > 0);
    if (valid.length === 0) { toast.error("Agregá al menos una pieza con largo válido"); return; }
    try {
      const { data } = await api.post("/cuts/optimize", {
        stock_length: Number(stockLength), kerf: Number(kerf),
        pieces: valid.map((p) => ({ material: p.material, label: p.label, length: Number(p.length), quantity: Number(p.quantity) })),
        save, name: planName, work_order_id: workOrderId || null,
      });
      setResult(data);
      if (save) { toast.success("Plan de corte guardado"); loadPlans(); }
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadPlan = (plan) => {
    setStockLength(plan.request.stock_length);
    setKerf(plan.request.kerf);
    setPieces(plan.request.pieces.map((p) => ({ material: p.material || "", label: p.label, length: p.length, quantity: p.quantity })));
    setResult(plan.result);
    setPlanName(plan.name);
  };

  const labelColors = {};
  let colorIdx = 0;
  const getColor = (label) => {
    if (!labelColors[label]) labelColors[label] = COLORS[colorIdx++ % COLORS.length];
    return labelColors[label];
  };

  const doPrint = () => window.print();

  const exportPDF = () => {
    if (!result) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 15;
    const usableW = pageW - marginX * 2;
    let y = 18;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(company?.name || "Plan de corte", marginX, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    const infoLine = [company?.cuit && `CUIT ${company.cuit}`, company?.address, company?.phone].filter(Boolean).join("  ·  ");
    if (infoLine) { doc.text(infoLine, marginX, y); y += 5; }

    doc.setTextColor(0);
    doc.setFontSize(11);
    const wo = orderLabel(workOrderId);
    const job = orderJobName(workOrderId);
    doc.text(`${planName || "Plan de corte"} — ${new Date().toLocaleDateString("es-AR")}${wo ? `  ·  Orden ${wo}` : ""}`, marginX, y);
    y += 6;
    if (job) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(`Obra: ${job}`, marginX, y);
      doc.setFont("helvetica", "normal");
      y += 6;
    }

    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(`Barra de stock: ${result.stock_length}mm   ·   Espesor de corte: ${result.kerf}mm`, marginX, y);
    y += 5;
    doc.text(`Barras: ${result.total_bars}   ·   Piezas: ${result.total_pieces}   ·   Desperdicio: ${(result.total_waste_mm / 1000).toFixed(2)}m   ·   Aprovechamiento: ${result.efficiency_pct}%`, marginX, y);
    y += 9;
    doc.setTextColor(0);

    const barH = 8;
    doc.setFontSize(7.5);
    result.bars.forEach((bar) => {
      const listText = groupBarCuts(bar.cuts).map((g) => `${g.label}: ${g.length}mm${g.qty > 1 ? ` ×${g.qty}` : ""}`).join("   ·   ");
      const listLines = doc.splitTextToSize(listText, usableW);
      const blockH = barH + 8 + listLines.length * 3.2;
      if (y + blockH > pageH - 15) { doc.addPage(); y = 18; }

      doc.setFontSize(8.5);
      doc.setTextColor(90);
      doc.text(`Barra ${bar.index} · ${bar.material || "Sin especificar"} · ${result.stock_length}mm`, marginX, y);
      doc.text(`Sobrante: ${bar.waste}mm`, marginX + usableW, y, { align: "right" });
      y += 2;

      let x = marginX;
      doc.setDrawColor(255, 255, 255);
      bar.cuts.forEach((cut) => {
        const w = (cut.length / result.stock_length) * usableW;
        const [r, g, b] = hexToRgb(getColor(cut.label));
        doc.setFillColor(r, g, b);
        doc.rect(x, y, w, barH, "F");
        if (w > 8) {
          doc.setFontSize(6.5);
          doc.setTextColor(20);
          doc.text(`${cut.length}`, x + w / 2, y + barH / 2 + 1.2, { align: "center" });
        }
        x += w;
      });
      const wasteW = (bar.waste / result.stock_length) * usableW;
      if (wasteW > 0) {
        doc.setFillColor(235, 235, 235);
        doc.rect(x, y, wasteW, barH, "F");
      }
      doc.setDrawColor(60);
      doc.setLineWidth(0.25);
      doc.rect(marginX, y, usableW, barH);
      y += barH + 3;

      doc.setFontSize(7.5);
      doc.setTextColor(60);
      doc.text(listLines, marginX, y);
      y += listLines.length * 3.2 + 3;
    });

    doc.save(`plan-corte-${(planName || "sin-nombre").trim().replace(/\s+/g, "-").toLowerCase()}.pdf`);
  };

  return (
    <div data-testid="optimizer-page">
      <h1 className="font-heading font-bold uppercase text-2xl tracking-tight mb-2">Optimizador de cortes lineales</h1>
      <p className="text-sm text-neutral-400 mb-6">Ingresá el perfil de cada pieza y el sistema optimiza cada material por separado, sin mezclar perfiles incompatibles.</p>

      <div className="space-y-6">
        <div className="bg-[#171717] border border-neutral-800 rounded-md p-6 space-y-4 no-print">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Largo de barra (mm)</label>
              <Input type="number" value={stockLength} onChange={(e) => setStockLength(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="stock-length-input" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Espesor de corte (mm)</label>
              <Input type="number" value={kerf} onChange={(e) => setKerf(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="kerf-input" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-neutral-400 block mb-2">Piezas a cortar</label>
            <div className="hidden lg:grid grid-cols-[1fr_1fr_140px_120px_32px] gap-2 mb-1 px-1">
              <span className="text-[10px] uppercase font-bold text-neutral-500">Perfil o material</span>
              <span className="text-[10px] uppercase font-bold text-neutral-500">Nombre (opcional)</span>
              <span className="text-[10px] uppercase font-bold text-neutral-500">Largo (mm)</span>
              <span className="text-[10px] uppercase font-bold text-neutral-500">Cantidad</span>
              <span />
            </div>
            <div className="space-y-2">
              {pieces.map((p, i) => (
                <div key={i} className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_140px_120px_32px] gap-2 items-center bg-neutral-900/60 border border-neutral-800 rounded-md p-2">
                  <Input placeholder="Ej: Ángulo, Hierro T" value={p.material || ""} onChange={(e) => setPiece(i, "material", e.target.value)} className="bg-neutral-950 border-neutral-700 rounded-sm h-10 w-full col-span-2 lg:col-span-1" data-testid={`piece-material-${i}`} />
                  <Input placeholder="Ej: Parante, marco" value={p.label} onChange={(e) => setPiece(i, "label", e.target.value)} className="bg-neutral-950 border-neutral-700 rounded-sm h-10 w-full col-span-2 lg:col-span-1" data-testid={`piece-label-${i}`} />
                  <Input type="number" inputMode="decimal" min="1" placeholder="Largo mm" value={p.length} onChange={(e) => setPiece(i, "length", e.target.value)} className="bg-neutral-950 border-neutral-700 rounded-sm h-10 w-full text-base" data-testid={`piece-length-${i}`} />
                  <Input type="number" inputMode="numeric" min="1" step="1" placeholder="Cant." value={p.quantity} onChange={(e) => setPiece(i, "quantity", e.target.value)} className="bg-neutral-950 border-neutral-700 rounded-sm h-10 w-full text-base" data-testid={`piece-qty-${i}`} />
                  {pieces.length > 1
                    ? <button onClick={() => setPieces(pieces.filter((_, idx) => idx !== i))} className="text-neutral-500 hover:text-red-400 p-1 justify-self-end lg:justify-self-center" aria-label={`Eliminar pieza ${i + 1}`} data-testid={`piece-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                    : <span />}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500 mt-2">Usá el mismo nombre de perfil para las piezas que pueden compartir una barra.</p>
            <Button size="sm" variant="outline" onClick={() => setPieces([...pieces, { material: "", label: "", length: "", quantity: 1 }])}
              className="border-neutral-700 rounded-sm mt-2 text-xs hover:bg-neutral-800 hover:text-white" data-testid="add-piece-btn">
              <Plus className="w-3 h-3 mr-1" /> Agregar pieza
            </Button>
          </div>
          <div className="space-y-2 pt-2 border-t border-neutral-800">
            <Button onClick={() => optimize(false)} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="optimize-btn">
              <Scissors className="w-4 h-4 mr-2" /> Optimizar cortes
            </Button>
            <div className="flex gap-2">
              <Input placeholder="Nombre del plan" value={planName} onChange={(e) => setPlanName(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-sm h-9 flex-1" data-testid="plan-name-input" />
              <Button variant="outline" onClick={() => optimize(true)} className="border-neutral-700 rounded-sm h-9 text-xs hover:bg-neutral-800 hover:text-white" data-testid="save-plan-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> Guardar
              </Button>
            </div>
            <select value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)}
              className="w-full h-9 bg-neutral-900 border border-neutral-800 rounded-sm px-2 text-xs" data-testid="plan-order-select">
              <option value="">Sin vincular a una orden</option>
              {orders.map((o) => <option key={o.id} value={o.id}>{o.number} — {o.client_name}</option>)}
            </select>
          </div>
          {plans.length > 0 && (
            <div className="pt-2 border-t border-neutral-800">
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-2">Planes guardados</label>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {plans.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 flex-wrap border-b border-neutral-800/60 pb-1">
                    <button onClick={() => loadPlan(p)} className="flex-1 min-w-0 text-left text-xs px-2 py-1.5 rounded-sm hover:bg-neutral-800 text-neutral-300 flex justify-between" data-testid={`plan-item-${p.id}`}>
                      <span className="truncate">{p.name}</span>
                      <span className="text-neutral-500 font-mono shrink-0 ml-2">{p.result.total_bars} barras</span>
                    </button>
                    {orderLabel(p.work_order_id) && (
                      <button onClick={() => linkPlan(p.id, null)} className="text-[10px] font-mono text-[#F2B90D] px-1.5 py-0.5 border border-[#F2B90D]/40 rounded-sm shrink-0 hover:bg-[#F2B90D]/10" title="Click para desvincular de la orden" data-testid={`plan-order-badge-${p.id}`}>
                        {orderLabel(p.work_order_id)}
                      </button>
                    )}
                    <select value={p.assigned_to || ""} onChange={(e) => assignPlan(p.id, e.target.value)}
                      className="h-7 bg-neutral-900 border border-neutral-800 rounded-sm px-1 text-[10px] max-w-28" data-testid={`plan-assign-${p.id}`}>
                      <option value="">Sin operario</option>
                      {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <button onClick={() => removePlan(p)} className="text-red-400 hover:bg-red-500/10 p-1 rounded-sm shrink-0" title="Eliminar plan" data-testid={`plan-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {!result && (
            <div className="bg-[#111] border border-dashed border-neutral-800 rounded-md h-full min-h-[300px] flex items-center justify-center text-neutral-600 text-sm no-print">
              El resultado de la optimización aparece acá
            </div>
          )}
          {result && (
            <div className="space-y-6 print-area" data-testid="optimizer-result">
              <div className="print-only mb-2">
                <h2 className="text-lg font-bold">{company?.name || "Plan de corte"}</h2>
                {(company?.cuit || company?.address || company?.phone) && (
                  <p className="text-xs">{[company?.cuit && `CUIT ${company.cuit}`, company?.address, company?.phone].filter(Boolean).join(" · ")}</p>
                )}
              </div>
              <div className="bg-[#171717] border border-neutral-800 rounded-md p-4" data-testid="optimizer-result-header">
                <p className="text-sm text-neutral-400">
                  {planName || "Plan de corte"} — {new Date().toLocaleDateString("es-AR")}
                  {orderLabel(workOrderId) ? ` · Orden ${orderLabel(workOrderId)}` : ""}
                </p>
                {orderJobName(workOrderId) && (
                  <p className="text-lg font-heading font-bold text-[#F2B90D] mt-0.5">Obra: {orderJobName(workOrderId)}</p>
                )}
              </div>
              <div className="flex justify-end gap-2 no-print">
                <Button variant="outline" onClick={doPrint} className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="print-btn">
                  <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
                </Button>
                <Button variant="outline" onClick={exportPDF} className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="export-pdf-btn">
                  <FileDown className="w-3.5 h-3.5 mr-1" /> Exportar PDF
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ["Barras necesarias", result.total_bars],
                  ["Piezas", result.total_pieces],
                  ["Desperdicio", `${(result.total_waste_mm / 1000).toFixed(2)} m`],
                  ["Aprovechamiento", `${result.efficiency_pct}%`],
                ].map(([l, v]) => (
                  <div key={l} className="bg-[#171717] border border-neutral-800 rounded-md p-4">
                    <p className="text-2xl font-heading font-bold text-[#F2B90D]">{v}</p>
                    <p className="text-[11px] uppercase tracking-wider text-neutral-400 mt-1">{l}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#171717] border border-neutral-800 rounded-md p-6 space-y-5">
                {result.bars.map((bar) => (
                  <div key={bar.index} data-testid={`bar-${bar.index}`}>
                    <div className="flex justify-between text-xs text-neutral-400 mb-1.5">
                      <span className="font-mono">Barra {bar.index} · <strong className="text-[#F2B90D]">{bar.material || "Sin especificar"}</strong> · {result.stock_length}mm</span>
                      <span>Sobrante: <span className="font-mono text-neutral-300">{bar.waste}mm</span></span>
                    </div>
                    <div className="flex h-9 rounded-sm overflow-hidden border border-neutral-700 bg-neutral-900">
                      {bar.cuts.map((cut, ci) => (
                        <div key={ci} style={{ width: `${(cut.length / result.stock_length) * 100}%`, background: getColor(cut.label) }}
                          className="cut-piece flex items-center justify-center text-[10px] font-mono font-bold text-black/80 border-r border-black/30 overflow-hidden whitespace-nowrap"
                          title={`${cut.label}: ${cut.length}mm`}>
                          {cut.length}
                        </div>
                      ))}
                      <div style={{ width: `${(bar.waste / result.stock_length) * 100}%` }} className="waste-piece bg-neutral-800 flex items-center justify-center text-[9px] text-neutral-500" title={`Sobrante: ${bar.waste}mm`} />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {groupBarCuts(bar.cuts).map((g, gi) => (
                        <span key={gi} className="text-[10px] font-mono bg-neutral-900 border border-neutral-800 rounded-sm px-1.5 py-0.5 text-neutral-300 whitespace-nowrap">
                          {g.label}: {g.length}mm{g.qty > 1 ? ` ×${g.qty}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
